import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Grid,
    Button,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Paper,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import { getRequest, putRequest } from 'Includes/api/apicall';
import { GET_URL, PUT_URL } from 'Includes/urls';
import { useNavigate,useLocation } from 'react-router-dom';

/** Values are sent to API; backend normalizes (e.g. "in progress" → inprogress). */
const READ_STATUS_OPTIONS = ['all', 'read', 'unread'];
const TICKET_STATUS_OPTIONS = ['all', 'open', 'closed', 'resolved', 'in progress', 'reopen', 'on hold', 'ai_mode'];

function TicketList(props) {
    const { history } = props;
    const location = useLocation();
    console.log(location);
    const [tickets, setTickets] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [ticketCount, setTicketCount] = useState(0);
    const [page, setPage] = useState(() => {
        const savedPage = localStorage.getItem('ticketListPage');
        return savedPage ? parseInt(savedPage, 10) : 0;
    });
    const [pageSize, setPageSize] = useState(10);
    const [summaryData, setSummaryData] = useState({ overall: { open: 0, closed: 0, total: 0 }, company_wise: [] });
    const [activeTab, setActiveTab] = useState(0);
    const [selectedCoordinatorCompany, setSelectedCoordinatorCompany] = useState(null);

    useEffect(() => {
        localStorage.setItem('ticketListPage', page);
    }, [page]);

    const getTicketSummary = () => {
        const params = {};
        if (selectedCompany && selectedCompany !== 'all') {
            params.company = selectedCompany;
        }
        getRequest(GET_URL.ticket.api + 'summary/', params, props)
            .then(response => {
                if (response && response.status === 200) {
                    setSummaryData(response.data);
                }
            })
            .catch(error => {
                console.error('Error fetching ticket summary:', error);
            });
    };

    useEffect(() => {
        getCompanyList();
        getTicketSummary();
    }, []);

    useEffect(() => {
        const storedCompanyId = localStorage.getItem('selectedCompanyId') || 'all';
        setSelectedCompany(storedCompanyId);
    }, []);

    useEffect(() => {
        getTicketList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, selectedCompany, selectedFilter, selectedStatusFilter]);

    const getCompanyList = () => {
        const url = GET_URL.company.api;
        getRequest(url, { limit: 1000 }, props)
            .then(response => {
                if (response && response.status === 200) {
                    setCompanies(response.data.data || response.data);
                }
            })
            .catch(error => {
                console.error('Error fetching companies:', error);
            });
    };

    const getTicketList = () => {
        setLoading(true);
        getTicketSummary();

        const params = {
            ticket_list: true,
            is_active: true,
            limit: pageSize,
            pageno: page + 1,
        };
        if (selectedCompany && selectedCompany !== 'all') {
            params.company = selectedCompany;
        }
        if (selectedFilter && selectedFilter !== 'all') {
            params.read_status = selectedFilter;
        }
        if (selectedStatusFilter && selectedStatusFilter !== 'all') {
            params.ticket_status = selectedStatusFilter;
        }

        getRequest(GET_URL.ticket.api, params, props)
            .then((response) => {
                if (response && response.status === 200) {
                    const allTickets = response.data?.data || [];
                    const totalCount = response.data?.count || 0;
                    setTickets(allTickets);
                    setTicketCount(totalCount);
                } else {
                    resetTableData();
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching tickets:', error);
                resetTableData();
                setLoading(false);
            });
    };

    const resetTableData = () => {
        setTickets([]);
        setTicketCount(0);
    };

    const companyOptions = React.useMemo(() => {
        return [{ id: 'all', name: 'All Companies' }, ...companies];
    }, [companies]);

    const selectedCompanyOption = React.useMemo(() => {
        return companyOptions.find((company) => String(company.id) === String(selectedCompany)) || companyOptions[0] || null;
    }, [companyOptions, selectedCompany]);

    const handleMarkAsRead = async (ticketId) => {
        const url = `${PUT_URL.ticket.api}${ticketId}/`;
        try {
            await putRequest(url, { read_status: 'read' }, {});
        } catch (error) {
            console.error('Error marking ticket as read:', error);
        }
    };

    const handleTicketClick2 = async (ticketId, companyId) => {
        if (!companyId) {
            console.error('Company ID is missing!');
            return;
        }
        try {
            // Mark as read FIRST (awaited) — this saves the chatbot reply on the backend
            const url = `${PUT_URL.ticket.api}${ticketId}/`;
            await putRequest(url, { read_status: 'read' }, {});
        } catch (error) {
            console.error('Error marking ticket as read:', error);
        }
        try {
            localStorage.setItem('previousURL', location.pathname);
            navigate(`/ticket/list/${companyId}/detail/?ticket_id=${ticketId}`);
        } catch (error) {
            console.error('Error in handleTicketClick2:', error);
        }
    };

    const filteredCompanyWise = React.useMemo(() => {
        if (selectedCoordinatorCompany && selectedCoordinatorCompany.id !== 'all') {
            return (summaryData.company_wise || []).filter(
                (item) => String(item.company_id) === String(selectedCoordinatorCompany.id)
            );
        }
        return summaryData.company_wise || [];
    }, [summaryData.company_wise, selectedCoordinatorCompany]);

    const coordinatorSummaryCounts = React.useMemo(() => {
        let open = 0;
        let closed = 0;
        let total = 0;
        filteredCompanyWise.forEach((item) => {
            open += item.open || 0;
            closed += item.closed || 0;
            total += item.total || 0;
        });
        return { open, closed, total };
    }, [filteredCompanyWise]);

    const coordinatorRows = React.useMemo(() => {
        const groups = {};
        filteredCompanyWise.forEach((item) => {
            const coordinator = item.coordinator_name || 'Unassigned';
            if (!groups[coordinator]) {
                groups[coordinator] = {
                    id: coordinator,
                    coordinator_name: coordinator,
                    company_names: [],
                    open: 0,
                    closed: 0,
                    total: 0
                };
            }
            if (item.company_name && !groups[coordinator].company_names.includes(item.company_name)) {
                groups[coordinator].company_names.push(item.company_name);
            }
            groups[coordinator].open += item.open || 0;
            groups[coordinator].closed += item.closed || 0;
            groups[coordinator].total += item.total || 0;
        });
        return Object.values(groups).map((row, idx) => ({
            ...row,
            id: row.coordinator_name || idx,
            company_names_str: row.company_names.join(', ') || '-',
            slNo: idx + 1
        }));
    }, [filteredCompanyWise]);

    const coordinatorColumns = [
        { field: 'slNo', headerName: 'Sl No', width: 80 },
        { field: 'coordinator_name', headerName: 'Coordinator Name', width: 250, flex: 1 },
        {
            field: 'open',
            headerName: 'Open Tickets',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const count = params.value || 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            backgroundColor: count > 0 ? '#eff6ff' : '#f2f4f7',
                            color: count > 0 ? '#1d4ed8' : '#475467',
                            border: '1px solid',
                            borderColor: count > 0 ? '#bfdbfe' : '#eaecf0',
                        }}
                    />
                );
            }
        },
        {
            field: 'closed',
            headerName: 'Closed Tickets',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const count = params.value || 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            backgroundColor: count > 0 ? '#f0fdf4' : '#f2f4f7',
                            color: count > 0 ? '#15803d' : '#475467',
                            border: '1px solid',
                            borderColor: count > 0 ? '#bbf7d0' : '#eaecf0',
                        }}
                    />
                );
            }
        },
        {
            field: 'total',
            headerName: 'Total Tickets',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const count = params.value || 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            backgroundColor: count > 0 ? '#f9fafb' : '#f2f4f7',
                            color: count > 0 ? '#111827' : '#475467',
                            border: '1px solid',
                            borderColor: count > 0 ? '#d1d5db' : '#eaecf0',
                        }}
                    />
                );
            }
        }
    ];

    const columns = [
        { field: 'id', headerName: 'Ticket Number', width: 150 },
        { field: 'company_name', headerName: 'Company Name', width: 200, flex: 1 },
        { field: 'username', headerName: 'User Name', width: 180 },

        {
            field: 'read_status',
            headerName: 'Read Status',
            width: 150,
            renderCell: (params) => {
                const isRead = params.value?.toLowerCase().startsWith('read');
                return (
                    <Chip
                        label={isRead ? 'Read' : 'Unread'}
                        color={isRead ? 'success' : 'error'}
                        size="small"
                    />
                );
            }
        },

        {
            field: 'ticket_status',
            headerName: 'Ticket Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color="primary"
                    sx={{ textTransform: 'capitalize' }}
                />
            )
        },

        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const ticketId = params.row.id;
                const companyId = params.row.companyId || params.row.company_id || selectedCompany;

                return (
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                        onClick={() => {
                            handleTicketClick2(ticketId, companyId);
                        }}
                    >
                        View
                    </Button>
                );
            }
        }
    ];

    return (
        <Box sx={{ p: 3 }}>

            {/* Header */}
            <Grid container alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h5" fontWeight={600}>
                        {selectedCompany && selectedCompany !== 'all' ? 'Company Tickets' : 'Tickets'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedCompany && selectedCompany !== 'all' ? 'Manage tickets for selected company' : 'Manage and track all support tickets'}
                    </Typography>
                </Grid>
            </Grid>

            {/* Navigation Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                    setActiveTab(newValue);
                    setPage(0);
                }}
                sx={{
                    mb: 3,
                    borderBottom: '1px solid #e2e8f0',
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        minWidth: 120,
                    }
                }}
            >
                <Tab label="Tickets" />
                <Tab label="Coordinator Assignments" />
            </Tabs>

            {activeTab === 0 && (
                <>
                    {/* Ticket Summary Section (Dashboard Format) */}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {/* Total Tickets KPI Card */}
                        <Grid item xs={12} sm={4}>
                            <Paper
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.02)',
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)',
                                    border: '1px solid #e9ecef',
                                    textAlign: 'center',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        boxShadow: '0 8px 20px rgba(16, 24, 40, 0.04)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                <Typography variant="h5" fontWeight={800} color="#212529">
                                    {summaryData.overall?.total || 0}
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="#495057" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', mt: 0.5, display: 'block', letterSpacing: 0.5 }}>
                                    Total Tickets
                                </Typography>
                            </Paper>
                        </Grid>

                        {/* Open Tickets KPI Card */}
                        <Grid item xs={12} sm={4}>
                            <Paper
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.02)',
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                    border: '1px solid #bfdbfe',
                                    textAlign: 'center',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        boxShadow: '0 8px 20px rgba(16, 24, 40, 0.04)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                <Typography variant="h5" fontWeight={800} color="#1d4ed8">
                                    {summaryData.overall?.open || 0}
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="#1e40af" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', mt: 0.5, display: 'block', letterSpacing: 0.5 }}>
                                    Open Tickets
                                </Typography>
                            </Paper>
                        </Grid>

                        {/* Closed Tickets KPI Card */}
                        <Grid item xs={12} sm={4}>
                            <Paper
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.02)',
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                    border: '1px solid #bbf7d0',
                                    textAlign: 'center',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        boxShadow: '0 8px 20px rgba(16, 24, 40, 0.04)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                <Typography variant="h5" fontWeight={800} color="#15803d">
                                    {summaryData.overall?.closed || 0}
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="#166534" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', mt: 0.5, display: 'block', letterSpacing: 0.5 }}>
                                    Closed Tickets
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Filters */}
                    <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    size="small"
                                    options={companyOptions}
                                    value={selectedCompanyOption}
                                    getOptionLabel={(option) => option?.name || ''}
                                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                    onChange={(_, option) => {
                                        const newCompanyId = option?.id || 'all';
                                        setSelectedCompany(newCompanyId);

                                        if (newCompanyId && newCompanyId !== 'all') {
                                            localStorage.setItem('selectedCompanyId', newCompanyId);
                                            localStorage.removeItem('previousURL');
                                        } else {
                                            localStorage.removeItem('selectedCompanyId');
                                        }

                                        setPage(0);
                                    }}
                                    sx={{ maxWidth: 290 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Company"
                                            placeholder="Search company"
                                        />
                                    )}
                                    ListboxProps={{
                                        style: { maxHeight: 320 },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ maxWidth: 240 }}>
                                    <InputLabel>Read Status</InputLabel>
                                    <Select
                                        value={selectedFilter}
                                        label="Read Status"
                                        onChange={(e) => {
                                            setPage(0);
                                            setSelectedFilter(e.target.value);
                                        }}
                                    >
                                        {READ_STATUS_OPTIONS.map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option.toUpperCase()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ maxWidth: 240 }}>
                                    <InputLabel>Ticket Status</InputLabel>
                                    <Select
                                        value={selectedStatusFilter}
                                        label="Ticket Status"
                                        onChange={(e) => {
                                            setPage(0);
                                            setSelectedStatusFilter(e.target.value);
                                        }}
                                    >
                                        {TICKET_STATUS_OPTIONS.map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option.toUpperCase()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Table */}
                    <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <DataGrid
                            rows={tickets}
                            columns={columns}
                            loading={loading}
                            pagination
                            paginationMode="server"
                            rowCount={ticketCount}
                            page={page}
                            pageSize={pageSize}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newPageSize) => {
                                setPageSize(newPageSize);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            autoHeight
                            rowHeight={42}
                            disableRowSelectionOnClick
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f5f7fb',
                                    fontWeight: 600,
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#fafafa',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #f0f0f0',
                                }
                            }}
                        />
                    </Paper>
                </>
            )}

            {activeTab === 1 && (
                <>
                    {/* Company Search Filter for Coordinator Tab */}
                    <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Autocomplete
                                    size="small"
                                    options={companyOptions}
                                    value={selectedCoordinatorCompany || companyOptions[0]}
                                    getOptionLabel={(option) => option?.name || ''}
                                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                    onChange={(_, option) => {
                                        setSelectedCoordinatorCompany(option);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Company"
                                            placeholder="Search company"
                                        />
                                    )}
                                    ListboxProps={{
                                        style: { maxHeight: 320 },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#475467" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem', mb: 2 }}>
                            Tickets Summary By Coordinator
                        </Typography>
                        <DataGrid
                            rows={coordinatorRows}
                            columns={coordinatorColumns}
                            autoHeight
                            rowHeight={46}
                            disableRowSelectionOnClick
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f5f7fb',
                                    fontWeight: 600,
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#fafafa',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #f0f0f0',
                                }
                            }}
                        />
                    </Paper>
                </>
            )}
        </Box>
    );
}

export default TicketList;
